import { Controller } from "@hotwired/stimulus"

// ui--media-picker — carries a selection from a Ui::MediaLibraryComponent
// inside a modal into the hidden inputs of the form the modal opened over.
//
// The whole reason this exists rather than letting the library's own inputs
// submit: the library is inside a <dialog> that overlays somebody else's form,
// and a selection must not reach that form until Confirm. So the cards' inputs
// are the selection UI and these hidden inputs are the answer, and only
// `confirm()` copies one into the other. `cancel()` puts the cards back to
// whatever the hidden inputs still say, which is what makes opening the modal
// by accident harmless.
//
// Targets:
//   inputs      - the wrapper holding the hidden inputs the host form reads
//   summary     - the selected-asset cards drawn beside the trigger
//   placeholder - the "nothing selected" line
//
// Values:
//   multiple   - checkbox grid (many ids) vs radio grid (one)
//   name       - the hidden input name; already "field[]" when multiple
//   initial    - the ids selected when the page rendered, as JSON
//   emptyLabel - unused here; kept so a host can localise a re-render
export default class extends Controller {
  static targets = ["inputs", "summary", "placeholder"]

  static values = {
    multiple: { type: Boolean, default: false },
    name: String,
    initial: { type: Array, default: [] },
    emptyLabel: { type: String, default: "" },
  }

  connect() {
    this._committed = [...this.initialValue]
  }

  open() {
    // Re-sync before showing. The committed ids are the truth; the cards may
    // still be wearing a selection from a previous open that was cancelled.
    this._applyToCards(this._committed)
    this._modal?.open()
  }

  confirm() {
    this._committed = this._libraryIds()
    this._writeInputs(this._committed)
    this._updateSummary()
    this._modal?.close()
  }

  cancel() {
    this._applyToCards(this._committed)
    this._modal?.close()
  }

  // --- the modal and the library, found by scope rather than by id ---------
  //
  // Both are rendered by this component's own template, so they are always
  // inside this element. Querying for them beats threading two more id values
  // through the ERB, and it keeps a second picker on the same page from
  // reaching into the first one's modal.

  get _modalElement() {
    return this.element.querySelector("dialog")
  }

  get _modal() {
    const el = this._modalElement
    if (!el) return null
    return this.application.getControllerForElementAndIdentifier(el, "ui--modal")
  }

  get _libraryElement() {
    return this.element.querySelector('[data-controller~="ui--media-library"]')
  }

  get _library() {
    const el = this._libraryElement
    if (!el) return null
    return this.application.getControllerForElementAndIdentifier(
      el,
      "ui--media-library",
    )
  }

  _libraryIds() {
    const library = this._library
    if (library) return library.selectedIds

    // The library controller has not connected yet (a modal opened in the same
    // tick as the page load). Read the DOM directly rather than committing an
    // empty selection over a real one.
    return Array.from(
      this.element.querySelectorAll("[data-media-asset-id] input:checked"),
    ).map((input) => input.value)
  }

  _cardInputs() {
    return Array.from(
      this.element.querySelectorAll("[data-media-asset-id] input"),
    )
  }

  _applyToCards(ids) {
    this._cardInputs().forEach((input) => {
      input.checked = ids.includes(input.value)
    })
    // Keep the library's own count line honest — it listens to `change` on the
    // inputs, and setting `.checked` in script fires nothing.
    this._library?.toggle()
  }

  _writeInputs(ids) {
    if (!this.hasInputsTarget) return

    this.inputsTarget.replaceChildren()

    // An empty selection still submits one empty value. Without it a form that
    // had an image and now has none sends no key at all, and the server cannot
    // tell "unchanged" from "cleared" — the same reason the ERB renders one.
    const values = ids.length > 0 ? ids : [""]

    values.forEach((value) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = this.nameValue
      input.value = value
      this.inputsTarget.appendChild(input)
    })
  }

  // The summary cards are server-rendered for the ids the page loaded with.
  // After a confirm they are stale, and this component cannot build a card in
  // the browser — it has no thumbnail URL for an id it was never given. So it
  // moves the chosen cards out of the library and into the summary, which is
  // the same markup, already rendered, with the right picture in it.
  _updateSummary() {
    if (!this.hasSummaryTarget) return

    const grid = this.summaryTarget.querySelector("div")
    if (!grid) return

    grid.replaceChildren()

    this._committed.forEach((id) => {
      const card = this.element.querySelector(
        `[data-media-asset-id="${CSS.escape(id)}"]`,
      )
      if (!card) return

      const clone = card.cloneNode(true)
      clone.removeAttribute("id")
      clone.removeAttribute("data-ui--media-library-target")
      clone
        .querySelectorAll("[data-media-asset='selection'], input")
        .forEach((node) => node.remove())
      grid.appendChild(clone)
    })

    const empty = this._committed.length === 0
    this.summaryTarget.classList.toggle("hidden", empty)
    if (this.hasPlaceholderTarget) {
      this.placeholderTarget.classList.toggle("hidden", !empty)
    }
  }
}
